import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useTable, useSortBy, useGlobalFilter, usePagination } from 'react-table';
import { useHistory, useParams } from 'react-router-dom';
import { Badge, Spinner, Button, Alert } from 'react-bootstrap';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { NoData } from '../../../components/SharedComponents/NoData';
import PageTitle from '../../../layouts/PageTitle';
import { GlobalFilter } from '../Invoices/GlobalFilter';
import swal from 'sweetalert';

// Lucide React Icons
import {
  Plus,
  RefreshCw,
  Loader2,
  FileText,
  Building,
  Receipt,
  CreditCard,
  Clock,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

export const SupplierInvoiceList = () => {
  const { companyId } = useParams();
  const [supplierInvoices, setSupplierInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all');
  const [metrics, setMetrics] = useState(null);

  const history = useHistory();

  // Helper function to format dates consistently
  const formatDate = (dateValue) => {
    if (!dateValue) return '-';

    // If it's already in YYYY-MM-DD format, return as is
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }

    // If it's an ISO string or Date object, format it
    try {
      const date = new Date(dateValue);
      return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
    } catch (error) {
      console.warn('Invalid date format:', dateValue);
      return dateValue || '-';
    }
  };

  // Function to fetch supplier invoices and metrics
  const fetchSupplierInvoiceData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch both invoices and metrics in parallel
      const [invoicesResponse, metricsResponse] = await Promise.allSettled([
        axios.get(getApiUrl(`${companyId}/purchasing/supplier-invoices`), { withCredentials: true }),
        axios.get(getApiUrl(`${companyId}/purchasing/supplier-invoices/metrics`), { withCredentials: true })
      ]);

      console.log('🔍 DEBUG - Supplier Invoice API Response:', {
        invoicesStatus: invoicesResponse.status,
        metricsStatus: metricsResponse.status,
        invoicesCount: invoicesResponse.status === 'fulfilled' ? invoicesResponse.value.data.length : 0,
        firstInvoice: invoicesResponse.status === 'fulfilled' && invoicesResponse.value.data.length > 0
          ? invoicesResponse.value.data[0] : null
      });

      // Set invoices data
      if (invoicesResponse.status === 'fulfilled') {
        setSupplierInvoices(invoicesResponse.value.data);
      } else {
        console.error('Error fetching supplier invoices:', invoicesResponse.reason);
        setError('Det gick inte att hämta leverantörsfakturorna. Försök igen senare.');
      }

      // Set metrics data
      if (metricsResponse.status === 'fulfilled') {
        setMetrics(metricsResponse.value.data.data);
        console.log('🔍 DEBUG - Metrics data:', metricsResponse.value.data.data);
      } else {
        console.error('Error fetching metrics:', metricsResponse.reason);
        // Don't set error for metrics failure, just log it
      }

    } catch (err) {
      setError('Det gick inte att hämta leverantörsfakturorna. Försök igen senare.');
      console.error('Error fetching supplier invoice data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [companyId]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchSupplierInvoiceData();
  };

  useEffect(() => {
    fetchSupplierInvoiceData();
  }, [fetchSupplierInvoiceData]);

  const handleRowClick = (row) => {

    // Navigate to view/edit supplier invoice based on status
    //if (row.original.status === 'Verification Created') {
    history.push(`/${companyId}/supplier-invoices/${row.original.invoice_id}`);
    //} else {
    //  history.push(`/${companyId}/edit-supplier-invoice/${row.original.invoice_id}`);
    //}
  };

  const handleDelete = async (invoiceId) => {
    swal({
      title: "Är du säker?",
      text: "Om du tar bort leverantörsfakturan kommer den att tas bort permanent från systemet.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    })
      .then(async (willDelete) => {
        if (willDelete) {
          setLoading(true);
          const deleteApiUrl = getApiUrl(`${companyId}/purchasing/supplier-invoices/${invoiceId}`);
          try {
            await axios.delete(deleteApiUrl);
            // Refresh data after successful deletion
            await fetchSupplierInvoiceData();
          } catch (err) {
            console.error('Error deleting supplier invoice:', err);
            setError('Det gick inte att ta bort leverantörsfakturan. Försök igen senare.');
          } finally {
            setLoading(false);
          }
        }
      });
  };

  // Filter supplier invoices based on status
  const filteredSupplierInvoices = useMemo(() => {
    if (statusFilter === 'all') return supplierInvoices;
    return supplierInvoices.filter(invoice => invoice.status === statusFilter);
  }, [supplierInvoices, statusFilter]);

  // Calculate summary metrics for filtered data
  const filteredTotals = useMemo(() => {
    const totalAmount = filteredSupplierInvoices.reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0);
    const statusCounts = filteredSupplierInvoices.reduce((acc, inv) => {
      acc[inv.status] = (acc[inv.status] || 0) + 1;
      return acc;
    }, {});

    return {
      totalAmount,
      totalCount: filteredSupplierInvoices.length,
      statusCounts
    };
  }, [filteredSupplierInvoices]);

  // Define table columns
  const COLUMNS = useMemo(() => [
    {
      Header: 'Fakturanummer',
      accessor: 'invoice_number',
      Cell: ({ value, row }) => {
        const hasAttachments = row.original.attachment_count > 0;
        const hasVerification = row.original.has_verification;

        return (
          <div className="d-flex align-items-center">
            <div
              className="me-3 d-flex align-items-center justify-content-center"
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                backgroundColor: '#f59e0b15',
                color: '#f59e0b'
              }}
            >
              <Receipt size={18} />
            </div>
            <div className="flex-grow-1">
              <div className="d-flex align-items-center gap-2">
                <span style={{ fontWeight: '600', fontSize: '0.875rem', color: '#3b82f6' }}>
                  {value || '-'}
                </span>
                {hasAttachments && (
                  <div
                    className="attachment-indicator"
                    title={`${row.original.attachment_count} bilaga(r)`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '0.125rem 0.375rem',
                      backgroundColor: '#e0f2fe',
                      borderRadius: '12px',
                      fontSize: '0.6875rem',
                      color: '#0369a1'
                    }}
                  >
                    📎 {row.original.attachment_count}
                  </div>
                )}
                {hasVerification && (
                  <div
                    className="verification-indicator"
                    title="Verifikation skapad"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '0.125rem 0.375rem',
                      backgroundColor: '#dcfce7',
                      borderRadius: '12px',
                      fontSize: '0.6875rem',
                      color: '#166534'
                    }}
                  >
                    ✓ Verifierad
                  </div>
                )}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                Leverantörsfaktura
                {row.original.invoice_date && ` • ${formatDate(row.original.invoice_date)}`}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      Header: 'Leverantör',
      accessor: 'supplier_name',
      Cell: ({ value, row }) => (
        <div>
          <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
            {value || '-'}
          </div>
          {row.original.supplier_orgnumber && (
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              Org.nr: {row.original.supplier_orgnumber}
            </div>
          )}
        </div>
      ),
    },
    {
      Header: 'Fakturadatum',
      accessor: 'invoice_date',
      Cell: ({ value }) => (
        <span style={{ fontSize: '0.875rem' }}>
          {formatDate(value)}
        </span>
      ),
    },
    {
      Header: 'Förfallodatum',
      accessor: 'due_date',
      Cell: ({ value, row }) => {
        const isOverdue = row.original.is_overdue;
        const daysUntilDue = row.original.days_until_due;

        return (
          <div>
            <span style={{
              fontSize: '0.875rem',
              color: isOverdue ? '#dc2626' : '#374151'
            }}>
              {formatDate(value)}
            </span>
            {isOverdue && (
              <div style={{ fontSize: '0.6875rem', color: '#dc2626', fontWeight: '500' }}>
                {Math.abs(daysUntilDue)} dagar försenad
              </div>
            )}
            {!isOverdue && daysUntilDue <= 7 && daysUntilDue > 0 && (
              <div style={{ fontSize: '0.6875rem', color: '#f59e0b', fontWeight: '500' }}>
                {daysUntilDue} dagar kvar
              </div>
            )}
          </div>
        );
      },
    },
    {
      Header: 'Belopp',
      accessor: 'amount',
      Cell: ({ value }) => (
        <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
          {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(parseFloat(value) || 0)}
        </span>
      ),
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ value }) => {
        const getStatusConfig = (status) => {
          switch (status) {
            case 'Scanned':
              return { bg: '#f3f4f6', color: '#374151', text: 'Skannad' };
            case 'Pending Approval':
              return { bg: '#fef3c7', color: '#d97706', text: 'Väntar godkännande' };
            case 'Approved':
              return { bg: '#dbeafe', color: '#1e40af', text: 'Godkänd' };
            case 'Pending Payment':
              return { bg: '#fef3c7', color: '#d97706', text: 'Väntar betalning' };
            case 'Paid':
              return { bg: '#d1fae5', color: '#065f46', text: 'Betald' };
            case 'Verification Created':
              return { bg: '#dcfce7', color: '#166534', text: 'Verifikation skapad' };
            case 'Overdue':
              return { bg: '#fee2e2', color: '#991b1b', text: 'Förfallen' };
            default:
              return { bg: '#f3f4f6', color: '#374151', text: value };
          }
        };

        const config = getStatusConfig(value);

        return (
          <span
            style={{
              fontSize: '0.75rem',
              fontWeight: '500',
              padding: '0.25rem 0.75rem',
              borderRadius: '1rem',
              backgroundColor: config.bg,
              color: config.color,
            }}
          >
            {config.text}
          </span>
        );
      },
    }
  ], []);

  const tableInstance = useTable(
    {
      columns: COLUMNS,
      data: filteredSupplierInvoices,
      initialState: { pageSize },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    gotoPage,
    pageCount,
    setPageSize: setTablePageSize,
    setGlobalFilter,
  } = tableInstance;

  const { globalFilter, pageIndex } = state;

  // Enhanced pagination controls
  const PaginationControls = () => (
    <div
      style={{
        backgroundColor: '#fff',
        borderTop: '1px solid #f1f5f9',
        padding: '1rem 1.5rem'
      }}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <select
            className="form-select form-select-sm"
            style={{
              width: 'auto',
              fontSize: '0.875rem',
              border: '1px solid #e5e7eb',
              borderRadius: '6px'
            }}
            value={pageSize}
            onChange={e => {
              setPageSize(Number(e.target.value));
              setTablePageSize(Number(e.target.value));
            }}
          >
            {[10, 20, 30, 40, 50].map(size => (
              <option key={size} value={size}>{size} per sida</option>
            ))}
          </select>
          <span style={{ fontSize: '0.875rem', color: '#64748b' }}>
            Visar {page.length} av {filteredSupplierInvoices.length} leverantörsfakturor
          </span>
        </div>

        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            {'<<'}
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            style={{ fontSize: '0.75rem' }}
          >
            Föregående
          </Button>
          <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
            Sida {pageIndex + 1} av {pageOptions.length}
          </span>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => nextPage()}
            disabled={!canNextPage}
            style={{ fontSize: '0.75rem' }}
          >
            Nästa
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            {'>>'}
          </Button>
        </div>
      </div>
    </div>
  );

  if (loading && !refreshing) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .table td {
          vertical-align: middle !important;
        }
        
        .clickable-row {
          cursor: pointer;
        }
        
        .clickable-row:hover {
          background-color: #f8f9fa;
        }
        
        .btn-outline-secondary {
          border-color: #6c757d !important;
        }
        
        .btn-outline-secondary:hover {
          background-color: #6c757d !important;
          border-color: #6c757d !important;
          color: #fff !important;
        }
        
        /* Add a refresh button animation */
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .spin {
          animation: spin 1s linear infinite;
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.25rem;
        }
        
        .stat-card {
          background: white;
          border: none;
          border-radius: 8px;
          padding: 1.5rem;
          text-align: center;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          position: relative;
          overflow: hidden;
        }

        .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--accent-color, #f59e0b), var(--accent-color-light, #fbbf24));
        }
        
        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        }
        
        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
          font-size: 1.125rem;
          position: relative;
        }

        .stat-icon::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 12px;
          background: inherit;
          opacity: 0.1;
        }
        
        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 0.375rem;
          color: #1f2937;
        }
        
        .stat-label {
          color: #6b7280;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 0.25rem;
        }

        .stat-subtitle {
          color: #9ca3af;
          font-size: 0.6875rem;
          font-weight: 400;
        }
        
        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          }
        }

        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr 1fr;
          }
        }
      `}</style>
      <PageTitle activeMenu="Leverantörsfakturor" motherMenu="Fakturor" />
      <div className="col-12">
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {supplierInvoices.length === 0 && !loading ? (
          <>
            <NoData message="Det finns inga leverantörsfakturor att visa. Skapa en för att kunna se dem här." />
            <div className="d-flex justify-content-center p-4">
              <Button variant="primary" onClick={() => history.push(`/${companyId}/create-supplier-invoices`)}>
                <Plus size={16} className="me-2" />
                Skapa ny leverantörsfaktura
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Financial Dashboard for Expenses */}
            {metrics && (
              <div className="stats-grid mb-4">
                {/* Total Pending Payments */}
                <div className="stat-card" style={{ '--accent-color': '#f59e0b', '--accent-color-light': '#fbbf24' }}>
                  <div className="stat-icon" style={{ background: '#fffbeb', color: '#f59e0b' }}>
                    <Clock size={24} />
                  </div>
                  <div className="stat-value" style={{ color: '#f59e0b' }}>
                    {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(metrics.total_pending_amount || 0)}
                  </div>
                  <div className="stat-label">Väntande Betalningar</div>
                  <div className="stat-subtitle">
                    {metrics.pending_payment_count || 0} leverantörsfakturor
                  </div>
                </div>

                {/* Total Overdue */}
                <div className="stat-card" style={{ '--accent-color': '#dc2626', '--accent-color-light': '#ef4444' }}>
                  <div className="stat-icon" style={{ background: '#fef2f2', color: '#dc2626' }}>
                    <AlertTriangle size={24} />
                  </div>
                  <div className="stat-value" style={{ color: '#dc2626' }}>
                    {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(metrics.total_overdue_amount || 0)}
                  </div>
                  <div className="stat-label">Förfallna</div>
                  <div className="stat-subtitle">
                    {metrics.overdue_count || 0} förfallna fakturor
                  </div>
                </div>

                {/* Processing Efficiency */}
                <div className="stat-card" style={{ '--accent-color': '#059669', '--accent-color-light': '#10b981' }}>
                  <div className="stat-icon" style={{ background: '#f0fdf4', color: '#059669' }}>
                    <CheckCircle size={24} />
                  </div>
                  <div className="stat-value" style={{ color: '#059669' }}>
                    {metrics.processing_efficiency || 0}%
                  </div>
                  <div className="stat-label">Hanteringseffektivitet</div>
                  <div className="stat-subtitle">
                    {metrics.processed_count || 0} av {metrics.total_invoices || 0} bearbetade
                  </div>
                </div>

                {/* Total Approved Amount */}
                <div className="stat-card" style={{ '--accent-color': '#0284c7', '--accent-color-light': '#0ea5e9' }}>
                  <div className="stat-icon" style={{ background: '#f0f9ff', color: '#0284c7' }}>
                    <Building size={24} />
                  </div>
                  <div className="stat-value" style={{ color: '#0284c7' }}>
                    {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(metrics.total_approved_amount || 0)}
                  </div>
                  <div className="stat-label">Godkända Utgifter</div>
                  <div className="stat-subtitle">
                    Totalt godkänt belopp
                  </div>
                </div>
              </div>
            )}

            {/* Enhanced header with filters and actions */}
            <div className="d-flex align-items-center justify-content-between mb-4">
              {/* Status Filter and Stats */}
              <div className="d-flex align-items-center gap-4">
                <div className="d-flex align-items-center gap-2">
                  <span style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '500' }}>Visa:</span>
                  <select
                    className="form-select form-select-sm"
                    style={{
                      width: 'auto',
                      fontSize: '0.875rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px'
                    }}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">Alla ({filteredTotals.totalCount})</option>
                    <option value="Scanned">Skannade ({filteredTotals.statusCounts['Scanned'] || 0})</option>
                    <option value="Pending Approval">Väntar godkännande ({filteredTotals.statusCounts['Pending Approval'] || 0})</option>
                    <option value="Approved">Godkända ({filteredTotals.statusCounts['Approved'] || 0})</option>
                    <option value="Pending Payment">Väntar betalning ({filteredTotals.statusCounts['Pending Payment'] || 0})</option>
                    <option value="Paid">Betalda ({filteredTotals.statusCounts['Paid'] || 0})</option>
                    <option value="Verification Created">Verifikation skapad ({filteredTotals.statusCounts['Verification Created'] || 0})</option>
                    <option value="Overdue">Förfallna ({filteredTotals.statusCounts['Overdue'] || 0})</option>
                  </select>
                </div>

                {/* Summary Stats */}
                <div className="px-3 py-2 rounded" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                  <span style={{ fontSize: '0.75rem', color: '#16a34a' }}>Total summa:</span>
                  <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#15803d', marginLeft: '0.5rem' }}>
                    {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(filteredTotals.totalAmount)}
                  </span>
                </div>
              </div>

              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  onClick={handleRefresh}
                  disabled={refreshing}
                >
                  <RefreshCw size={16} className={refreshing ? "me-1 spin" : "me-1"} />
                  Uppdatera
                </Button>
                <Button
                  variant="primary"
                  onClick={() => history.push(`/${companyId}/create-supplier-invoices`)}
                >
                  <Plus size={16} className="me-2" />
                  Ny Leverantörsfaktura
                </Button>
              </div>
            </div>

            {refreshing && (
              <div className="text-center mb-3">
                <Loader2 size={16} className="me-1 spin" />
                Uppdaterar fakturlista...
              </div>
            )}

            {/* Enhanced table card */}
            <div className="card shadow-sm border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
              <div className="card-body p-0">
                {/* Search filter */}
                <div className="p-4 border-bottom" style={{ backgroundColor: '#fafbfc' }}>
                  <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
                </div>

                <div className="table-responsive">
                  <table {...getTableProps()} className="table table-hover mb-0">
                    <thead style={{ backgroundColor: '#fff', borderBottom: '2px solid #f1f5f9' }}>
                      {headerGroups.map((headerGroup, headerGroupIndex) => (
                        <tr {...headerGroup.getHeaderGroupProps()} key={`header-group-${headerGroupIndex}`}>
                          {headerGroup.headers.map((column, columnIndex) => (
                            <th
                              {...column.getHeaderProps(column.getSortByToggleProps())}
                              key={`header-${columnIndex}-${column.id}`}
                              className="fw-semibold border-0 py-4 px-4"
                              style={{
                                cursor: column.canSort ? 'pointer' : 'default',
                                userSelect: 'none',
                                fontSize: '0.875rem',
                                letterSpacing: '0.025em',
                                color: '#1e293b',
                                backgroundColor: 'transparent'
                              }}
                            >
                              <div className="d-flex align-items-center justify-content-between">
                                <span>{column.render('Header')}</span>
                                {column.canSort && (
                                  <span className="ms-2">
                                    {column.isSorted ? (
                                      column.isSortedDesc ?
                                        <i className="fa fa-sort-down" style={{ fontSize: '0.75rem', color: '#3b82f6' }} />
                                        :
                                        <i className="fa fa-sort-up" style={{ fontSize: '0.75rem', color: '#3b82f6' }} />
                                    ) : (
                                      <i className="fa fa-sort" style={{ fontSize: '0.75rem', color: '#cbd5e1', opacity: '0.6' }} />
                                    )}
                                  </span>
                                )}
                              </div>
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                      {page.map((row) => {
                        prepareRow(row);
                        return (
                          <tr
                            {...row.getRowProps()}
                            key={row.id}
                            onClick={() => handleRowClick(row)}
                            style={{
                              cursor: "pointer",
                              transition: 'all 0.2s ease',
                              borderBottom: '1px solid #f1f5f9'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8fafc';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            {row.cells.map(cell => (
                              <td
                                {...cell.getCellProps()}
                                key={cell.column.id}
                                className="py-4 px-4 align-middle border-0"
                                style={{
                                  fontSize: '0.875rem',
                                  color: '#374151'
                                }}
                              >
                                {cell.render('Cell')}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <PaginationControls />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default SupplierInvoiceList;
